/* Captures console output (log, warn, error, info) into a ring buffer.
   Logs are included in bug reports so we can debug issues without exposing technical details to users. */

const MAX_LOGS = 200;
const MAX_ENTRY_LENGTH = 500;

interface LogEntry {
	level: "log" | "warn" | "error" | "info";
	message: string;
	timestamp: number;
}

const logs: LogEntry[] = [];
let installed = false;

function formatArgs(args: unknown[]): string {
	return args
		.map((arg) => {
			if (typeof arg === "string") return arg;
			try {
				return JSON.stringify(arg);
			} catch {
				return String(arg);
			}
		})
		.join(" ")
		.slice(0, MAX_ENTRY_LENGTH);
}

function addEntry(level: LogEntry["level"], args: unknown[]) {
	logs.push({ level, message: formatArgs(args), timestamp: Date.now() });
	if (logs.length > MAX_LOGS) {
		logs.shift();
	}
}

export function installSessionLogCollector() {
	if (installed) return;
	installed = true;

	const original = {
		log: console.log.bind(console),
		warn: console.warn.bind(console),
		error: console.error.bind(console),
		info: console.info.bind(console),
	};

	console.log = (...args: unknown[]) => {
		addEntry("log", args);
		original.log(...args);
	};
	console.warn = (...args: unknown[]) => {
		addEntry("warn", args);
		original.warn(...args);
	};
	console.error = (...args: unknown[]) => {
		addEntry("error", args);
		original.error(...args);
	};
	console.info = (...args: unknown[]) => {
		addEntry("info", args);
		original.info(...args);
	};
}

export function getSessionLogs(): string {
	return logs
		.map((entry) => {
			const time = new Date(entry.timestamp).toISOString();
			return `[${time}] [${entry.level.toUpperCase()}] ${entry.message}`;
		})
		.join("\n");
}
